import { TableRootProps } from '@chakra-ui/react';
import {
  IPriceReqSchema,
  IPriceUnderProductSchemaUI,
  IQuoteItemRespSchema,
  IRateResSchema,
  OfferingTypesEnum,
  PriceModelEnum,
} from '@monetize/types/app';
import { IOrderedObj } from '@monetize/types/common';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { formatInteger, formatNumber } from '@monetize/utils/core';
import {
  FunctionComponent as FC,
  useEffect,
  useState,
  useTransition,
} from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { MdChevronRight, MdExpandMore } from 'react-icons/md';
import { OFFERINGS } from '../../../constants';
import {
  CUSTOMIZABLE_RATE_PRODUCT_TYPES,
  PRODUCT_TYPE_DISPLAY_SHORT,
} from '../../../constants/products';
import { useExperienceConfiguration } from '../../../hooks';
import { RatePriceTableRow } from '../../../routes/ProductCatalog/Offerings/components/Rate/RatePrice';
import { useFlags } from '../../../services/launchDarkly';
import { pluralize } from '../../../utils';
import { getNextTierPrice } from '../../../utils/product';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MTable,
  MTbody,
  MTd,
  MText,
  MTh,
  MThead,
  MTr,
} from '../../Monetize';
import { MCurrency } from '../../Monetize/MCurrency';
import { MPercent } from '../../Monetize/MPercent';
interface PricesDrawerBodyEditProductProps {
  offeringType: OfferingTypesEnum;
  offeringRate: IRateResSchema;
  productsObj: IOrderedObj<IQuoteItemRespSchema>;
  productWithPrices: IPriceUnderProductSchemaUI;
  isEditMode: boolean;
  shouldExpandAllProducts: boolean;
  pricingDisplayMaxThreshold: number;
  tableProps?: TableRootProps;
}

export const PricesDrawerBodyEditProduct: FC<
  PricesDrawerBodyEditProductProps
> = ({
  offeringRate,
  productWithPrices,
  productsObj,
  isEditMode = false,
  offeringType,
  shouldExpandAllProducts,
  pricingDisplayMaxThreshold,
  tableProps,
}: PricesDrawerBodyEditProductProps) => {
  const { enableBlockPricing } = useFlags();
  const {
    accountSpecificRateAllowPriceAdjustments,
    accountSpecificRateAllowTierAdjustments,
    lockPriceModelUpdateOnQuote,
  } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const isPoTOfferingType =
    offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL;

  const preventPriceAdjustments = !accountSpecificRateAllowPriceAdjustments;
  // If price cannot be changed, tier is also locked down
  const preventTierAdjustments =
    !accountSpecificRateAllowPriceAdjustments ||
    !accountSpecificRateAllowTierAdjustments;
  // If tier cannot be changed, price model is also locked down
  const preventPriceModelAdjustments =
    !accountSpecificRateAllowTierAdjustments || lockPriceModelUpdateOnQuote;

  const [isRateExpanded, setIsRateExpanded] = useState(false);
  const [isPending, startTransition] = useTransition();
  const {
    control,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
    watch,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `products.${productWithPrices.id}.prices`,
    keyName: 'pId',
  });

  const prices = watch(`products.${productWithPrices.id}.prices`);
  const isBlockPricing =
    enableBlockPricing &&
    prices?.some((pricing) => pricing.priceModel === PriceModelEnum.BLOCK);

  useEffect(() => {
    if (fields.length === 0) {
      append(
        getNextTierPrice({
          productId: productWithPrices.id,
          previousFrom: null,
          previousTo: null,
          previousAmount: null,
        }) as any,
      );
    }
  }, []);

  useEffect(() => {
    setIsRateExpanded(shouldExpandAllProducts);
  }, [shouldExpandAllProducts]);

  const handleRemovePrice = (index: number) => {
    // Make sure to clear any errors while removing a price tier
    const priceErrors = (errors?.products as Record<string, any>)?.[
      productWithPrices.id
    ]?.prices;

    if (priceErrors) {
      clearErrors(`products.${productWithPrices.id}.prices`);
    }

    const rowToRemove = getValues(
      `products.${productWithPrices.id}.prices.${index}`,
    ) as IPriceReqSchema;

    if (index !== fields.length - 1) {
      /** adjust next row from value with current row from value */
      setValue(
        `products.${productWithPrices.id}.prices.${index + 1}.from`,
        rowToRemove.from,
      );
    }

    remove(index);
  };

  return (
    <MBox>
      <MFlex
        align="center"
        justifyContent="space-between"
        py="3"
        pr="2"
        role="button"
        onClick={() => {
          if (isRateExpanded) {
            setIsRateExpanded(false);
          } else {
            startTransition(() => {
              setIsRateExpanded(true);
            });
          }
        }}
        cursor={'pointer'}
        _hover={{
          backgroundColor: 'blackAlpha.50',
        }}
      >
        <MBox display="flex" alignItems="center" pl={2}>
          <MCustomIconButton
            isLoading={
              isEditMode &&
              isPending &&
              fields.length > pricingDisplayMaxThreshold
            }
            btnSize={5}
            variant="ghost"
            icon={isRateExpanded ? MdExpandMore : MdChevronRight}
            aria-label="Toggle rate details"
            _hover={{
              background: 'transparent',
            }}
          />
          <MText as="h5" fontSize="md" fontWeight="bold">
            {productWithPrices.name}{' '}
            <MText as="span" fontWeight="normal">
              ({PRODUCT_TYPE_DISPLAY_SHORT[productWithPrices.productType]})
            </MText>
          </MText>
        </MBox>

        <MText as="span" justifyItems="end" fontWeight={'light'}>
          {formatInteger(fields.length)} {pluralize('tier', fields.length)}
        </MText>
      </MFlex>

      {isRateExpanded && (
        <MBox px={2} py={2}>
          <MTable
            size="sm"
            {...tableProps}
            css={{
              // Matches the Table theme's `thead th`/`tbody td` padding: what v2
              // rendered for header and body cells alike, its nested theme
              // selectors outranking this table's own `px: 2, py: 1` override.
              // Pinned so `size="sm"` can't shrink them, and kept equal so each
              // header label lines up with the input below it.
              '& th, & td': {
                py: '8px',
                px: '12px',
              },
              ...tableProps?.css,
            }}
          >
            <MThead>
              <MTr _hover={{ bg: 'transparent' }}>
                <MTh w="1%" />
                <MTh
                  fontWeight="bold"
                  textTransform="none"
                  fontSize="sm"
                  w="10rem"
                >
                  Pricing Model
                </MTh>
                <MTh
                  fontWeight="bold"
                  textTransform="none"
                  fontSize="sm"
                  {...(isPoTOfferingType && { w: '8rem' })}
                >
                  {isPoTOfferingType ? 'Minimum Total' : 'From'}
                </MTh>
                <MTh
                  fontWeight="bold"
                  textTransform="none"
                  fontSize="sm"
                  {...(isPoTOfferingType && { w: '8rem' })}
                >
                  {isPoTOfferingType ? 'Maximum Total' : 'Up to'}
                </MTh>
                {isBlockPricing && (
                  <MTh fontWeight="bold" textTransform="none" fontSize="sm">
                    Block Size
                  </MTh>
                )}
                <MTh
                  fontWeight="bold"
                  textTransform="none"
                  fontSize="sm"
                  {...(isPoTOfferingType && { w: '8rem' })}
                >
                  {isPoTOfferingType ? 'Percentage' : 'Amount'}
                </MTh>
                <MTh w="8" />
              </MTr>
            </MThead>
            <MTbody>
              {isEditMode &&
                fields.map((field, index) => {
                  const isLastRow = fields.length - 1 === index;
                  const lastRowIndex = fields.length - 1;
                  return (
                    <RatePriceTableRow
                      isPriceDrawer
                      offeringType={offeringType}
                      key={field.pId}
                      index={index}
                      id={field.pId}
                      isOpen
                      product={productWithPrices}
                      isLastRow={isLastRow}
                      lastRowIndex={lastRowIndex}
                      handleRemovePrice={handleRemovePrice}
                      isTierAdjustmentDisabled={preventTierAdjustments}
                      isPriceAdjustmentDisabled={preventPriceAdjustments}
                      isPriceModelDisabled={preventPriceModelAdjustments}
                      isBlockPricing={isBlockPricing}
                      generateNewPrice={(
                        previousFrom,
                        previousTo,
                        previousPriceModel,
                        previousAmount,
                        previousBlockSize,
                      ) =>
                        append(
                          getNextTierPrice({
                            productId: productWithPrices.id,
                            previousFrom,
                            previousTo,
                            previousPriceModel,
                            previousAmount,
                            previousBlockSize,
                          }) as any,
                        )
                      }
                      isLocked={
                        !CUSTOMIZABLE_RATE_PRODUCT_TYPES.has(
                          productWithPrices.productType,
                        )
                      }
                      currency={offeringRate.currency}
                    />
                  );
                })}

              {!isEditMode &&
                productWithPrices.prices.map(
                  (
                    { id, from, to, priceModel, amount, product, blockSize },
                    productIndex: number,
                  ) => {
                    const formattedFrom = formatNumber(from, {
                      maximumFractionDigits: 0,
                    });

                    const formattedTo =
                      formatNumber(to, {
                        maximumFractionDigits: 0,
                      }) || '∞';

                    const formattedBlockSize = formatNumber(blockSize);

                    return (
                      <MTr
                        key={`${productIndex}-range-${id}`}
                        _hover={{ bg: 'transparent' }}
                      >
                        <MTd verticalAlign="middle">
                          <MFlex align="center" minH={8}>
                            <MText color="tPurple.base">{`${productIndex + 1}.`}</MText>
                          </MFlex>
                        </MTd>
                        <MTd verticalAlign="middle">
                          <MFlex align="center" minH="8">
                            <MText>
                              {OFFERINGS.PRICE_MODEL_OPTIONS[priceModel]}
                            </MText>
                          </MFlex>
                        </MTd>
                        <MTd verticalAlign="middle">
                          <MFlex align="center" minH="8">
                            <MText>
                              {isPoTOfferingType
                                ? offeringRate.percentOfTotalConfig
                                    ?.minimumTotal
                                : formattedFrom}
                            </MText>
                          </MFlex>
                        </MTd>
                        <MTd verticalAlign="middle">
                          <MFlex align="center" minH="8">
                            <MText>
                              {isPoTOfferingType
                                ? offeringRate.percentOfTotalConfig
                                    ?.maximumTotal
                                : formattedTo}
                            </MText>
                          </MFlex>
                        </MTd>
                        {isBlockPricing && (
                          <MTd verticalAlign="middle">
                            <MFlex align="center" minH="8">
                              <MText>{formattedBlockSize}</MText>
                            </MFlex>
                          </MTd>
                        )}
                        <MTd verticalAlign="middle">
                          <MFlex align="center" minH="8">
                            {isPoTOfferingType && <MPercent amount={amount} />}
                            {!isPoTOfferingType && (
                              <MCurrency
                                amount={
                                  priceModel === PriceModelEnum.CUSTOM
                                    ? productsObj.byId[product.id]?.unitPrice
                                    : amount
                                }
                                options={{
                                  maximumFractionDigits: 'max',
                                  currency: offeringRate.currency,
                                }}
                              />
                            )}
                          </MFlex>
                        </MTd>
                        <MTd />
                      </MTr>
                    );
                  },
                )}
            </MTbody>
          </MTable>
        </MBox>
      )}
    </MBox>
  );
};
